import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Add as AddIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import _ from 'lodash';
import * as Ramda from 'ramda';
import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import CbButton from '../../../../Buttons/CbButton';
import { useQueryParams } from '../../../../providers/QueryParamsProvider';
import { prepQueryFilters } from './filter.utils';
import { ColumnSelect, FilterSelect, TableFilterInputFactory } from './helpers';

const FilterModal = (props: FilterModalProps) => {
  const formMethods = useForm<FilterFormFields>();
  const queryParams = useQueryParams();
  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'filters',
  });

  const handleApplyFilters = (data: FilterFormFields) => {
    const filters = prepQueryFilters(queryParams.query.f ?? {})(data.filters);

    const currentQuery = _.omit(queryParams.query, 'f');

    queryParams.push({}, { query: { ...currentQuery, ...filters } });
  };

  function handleAddRow() {
    fieldArrayMethods.append({
      columnName: {
        value: '',
        meta: {
          key: '',
          text: '',
          type: '',
          getListOptions: undefined,
        },
      },
      operator: '',
      value: '',
    });
  }

  React.useEffect(() => {
    if (formMethods.getValues().filters[0]?.columnName) {
      return;
    }
    handleAddRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={props.isOpen} maxWidth="sm" fullWidth disableEnforceFocus>
      <DialogTitle>
        <Box p={2} pb={0} pt={0}>
          Filter Columns
        </Box>
      </DialogTitle>

      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(
            Ramda.compose(props.onClose, handleApplyFilters)
          )}
        >
          <DialogContent style={{ padding: '0.5rem' }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={3.75}>
                Column Name
              </Grid>
              <Grid item xs={3.75}>
                Filter
              </Grid>
              <Grid item xs={3.75}>
                Value
              </Grid>
              {fieldArrayMethods.fields.map((field, idx) => {
                return (
                  <React.Fragment key={field.id}>
                    <Grid item xs={3.75}>
                      <ColumnSelect name={`filters.${idx}.columnName`} />
                    </Grid>
                    <Grid item xs={3.75}>
                      <FilterSelect name={`filters.${idx}.operator`} />
                    </Grid>
                    <Grid item xs={3.75}>
                      <TableFilterInputFactory name={`filters.${idx}.value`} />
                    </Grid>

                    <Grid item xs={0.75} style={{ paddingLeft: 10 }}>
                      <IconButton
                        size="small"
                        onClick={() => fieldArrayMethods.remove(idx)}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>

            <Box textAlign="right" mt={1}>
              <Button
                color="primary"
                onClick={handleAddRow}
                startIcon={<AddIcon />}
              >
                Add Filter
              </Button>
            </Box>
          </DialogContent>

          <DialogActions>
            <CbButton styleName="cancel" onClick={props.onClose}>
              Cancel
            </CbButton>
            <CbButton styleName="ctaButton" type="submit">
              Apply
            </CbButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default FilterModal;

type FilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export type FilterFormFields = {
  filters: FilterFields[];
};

export type FilterFields = {
  columnName: {
    value: string;
    meta: Meta;
  };
  operator: string;
  value: string;
};

type Meta = {
  key: string;
  text: string;
  type: string;
  getListOptions: (() => any) | undefined;
};
